import type { IDefaultFilter } from 'src/pages/tickets/tickets.types';

import { useState, useEffect } from 'react';

import { defaultFilter } from 'src/pages/tickets/tickets.types';
import {
  useGetLabelListMutation,
  useGetLabelDetailMutation,
} from 'src/pages/setting/slice/settingApi';

export interface IsetQueryParamsHandler {
  type: 'search' | 'pagination' | 'reset';
  value?: string;
}

export const useGetLabels = () => {
  const updatedFilter = {
    ...defaultFilter,
    filters: [...defaultFilter.filters],
  };

  const [getLabelList] = useGetLabelListMutation();
  const [getLabelDetail] = useGetLabelDetailMutation();

  const initialPaginationVal = { fetched: 0, total: Infinity };

  const [_recordList, setRecordList] = useState<any>([]);
  const [labelPagination, setLabelPagination] = useState(initialPaginationVal);
  const [queryParams, setQueryParams] = useState<IDefaultFilter>(updatedFilter);
  const [isFetching, setIsFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedLabelDtl, setSelectedLabelDtl] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const openListHandler = () => setOpen(true);
  const closeListHandler = () => {
    setOpen(false);
    queryParamsHandler({ type: 'reset' });
  };

  const resetPagination = () => {
    setLabelPagination(initialPaginationVal);
  };

  const queryParamsHandler = ({ type, value }: IsetQueryParamsHandler) => {
    switch (type) {
      case 'search': {
        setQueryParams((prev) => {
          if (prev.globalSearch !== value) {
            resetPagination();
            return { ...prev, page: 1, globalSearch: value || '' };
          }
          return prev;
        });
        break;
      }
      case 'pagination':
        setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }));
        break;
      case 'reset':
        setQueryParams(updatedFilter);
        break;
      default:
        break;
    }
  };

  const listScrollHandler = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    const isBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;

    const LabelHasMore = labelPagination.fetched < labelPagination.total;

    if (isBottom && !isFetching && LabelHasMore) {
      queryParamsHandler({ type: 'pagination' });
    }
  };

  const getLabelListFunc = async (params: IDefaultFilter) => {
    try {
      setIsFetching(true);

      const res = await getLabelList(params).unwrap();

      setLabelPagination((prev) => ({
        fetched: prev.fetched + res.list.length,
        total: res.total,
      }));

      const filterLabel = (list: any[], id: string) => {
        if (!selectedLabelDtl) return list;
        return list.filter((item) => item.id !== id);
      };

      const filteredList = filterLabel(res.list ?? [], selectedLabelDtl?.id ?? '');

      setRecordList((prev: any) => {
        const newList = params.globalSearch ? filteredList : [...prev, ...filteredList];
        const seen = new Set();
        return newList.filter((item) => {
          const isDup = seen.has(item.id);
          seen.add(item.id);
          return !isDup;
        });
      });
    } catch (error) {
      console.error('Error fetching label list:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const getLabelDetailFunc = async (labelId: string) => {
    if (!labelId) return;

    try {
      const res = await getLabelDetail(labelId).unwrap();

      if (res?.data) {
        setSelectedLabelDtl(res.data);
      }
    } catch (error) {
      console.error('Error fetching label detail:', error);
    }
  };

  useEffect(() => {
    if (open) {
      getLabelListFunc(queryParams);
    } else {
      if (selectedLabelDtl) {
        setRecordList([selectedLabelDtl]);
      } else {
        setRecordList([]);
      }
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, queryParams, selectedLabelDtl]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      queryParamsHandler({ type: 'search', value: searchValue });
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return {
    _recordList,
    isFetching,
    searchValue,
    open,
    resetPagination,
    queryParamsHandler,
    listScrollHandler,
    searchValueHandler: setSearchValue,
    openListHandler,
    closeListHandler,
    selectedLabelDtl,
    getLabelDetailFunc,
  };
};
